/**
 * Admin endpoints for managing public-API keys
 * (/api/admin/api-keys*). Gated server-side on `users.manage`.
 */
import { apiFetch } from "./apiClient";

// Catalog of recognised public-API scopes. Kept in sync with
// `ALL_SCOPES` in mttr/app_api/public_v1/auth.py + migration 069's
// header comment.
export const ALL_API_SCOPES = [
  "catalog:read",
  "inventory:read",
  "tags:read",
  "reports:read",
  "fleet:read",
  "departures:read",
] as const;

export type ApiScope = (typeof ALL_API_SCOPES)[number];

export const SCOPE_LABEL: Record<ApiScope, string> = {
  "catalog:read": "Catalog",
  "inventory:read": "Inventory",
  "tags:read": "Tags",
  "reports:read": "Reports",
  "fleet:read": "Fleet status",
  "departures:read": "Departures",
};

export interface ApiKeyRow {
  key_id: number;
  name: string;
  created_by: string;
  created_utc: string | null;
  last_used_utc: string | null;
  last_used_ip: string | null;
  revoked_utc: string | null;
  revoked_by: string | null;
  // Empty list = no restriction (back-compat with pre-069 keys).
  scopes: string[];
  // null = use the global default; 0 = unlimited; otherwise the
  // per-minute cap. Migration 070.
  rate_limit_per_minute: number | null;
}

export interface ApiKeysListResponse {
  company_id: number;
  keys: ApiKeyRow[];
}

export interface ApiKeyCreateResponse {
  key_id: number;
  name: string;
  company_id: number;
  scopes: string[];
  rate_limit_per_minute: number | null;
  raw_key: string;
  warning: string;
}

async function req<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  return apiFetch<T>(path, token, init);
}

export function listApiKeys(
  token: string, companyId?: number,
): Promise<ApiKeysListResponse> {
  const qs = companyId != null ? `?company_id=${companyId}` : "";
  return req<ApiKeysListResponse>(token, `/api/admin/api-keys${qs}`);
}

export function createApiKey(
  token: string,
  body: {
    name: string;
    company_id?: number;
    scopes?: string[];
    rate_limit_per_minute?: number | null;
  },
): Promise<ApiKeyCreateResponse> {
  return req<ApiKeyCreateResponse>(token, "/api/admin/api-keys", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function revokeApiKey(token: string, keyId: number): Promise<{ revoked: boolean }> {
  return req<{ revoked: boolean }>(token, `/api/admin/api-keys/${keyId}/revoke`, {
    method: "POST",
    body: "{}",
  });
}

export function setApiKeyRateLimit(
  token: string,
  keyId: number,
  rateLimitPerMinute: number | null,
): Promise<{ key_id: number; rate_limit_per_minute: number | null }> {
  return req(token, `/api/admin/api-keys/${keyId}/rate-limit`, {
    method: "POST",
    body: JSON.stringify({ rate_limit_per_minute: rateLimitPerMinute }),
  });
}
